import React from 'react';
import Card, { CardBody } from './ui/Card';
import Button from './ui/Button';

/**
 * ErrorBoundary — shows a recoverable fallback instead of a blank screen when a
 * component below it throws while rendering.
 *
 * This is deliberately a class component, the one exception to the
 * functional-components rule: React exposes error boundaries only through the
 * `getDerivedStateFromError` / `componentDidCatch` lifecycle pair, and there is
 * no hook equivalent (see https://react.dev/reference/react/Component).
 *
 * @param {React.ReactNode} children the subtree to guard
 * @param {string} title heading for the fallback
 */
class ErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false };
        this.handleReload = this.handleReload.bind(this);
    }

    static getDerivedStateFromError() {
        return { hasError: true };
    }

    componentDidCatch(error, errorInfo) {
        // Nothing collects client errors yet; surface it in the console so the
        // stack isn't lost while the user sees the fallback.
        console.error('Unhandled error in a page:', error, errorInfo?.componentStack);
    }

    handleReload() {
        window.location.reload();
    }

    render() {
        if (!this.state.hasError) {
            return this.props.children;
        }

        const { title = 'This page ran into a problem' } = this.props;

        return (
            <Card variant="solid" padding="lg" className="error-boundary" role="alert">
                <CardBody>
                    <h2>{title}</h2>
                    <p>
                        Something went wrong while displaying this page. Reloading usually fixes it —
                        if it keeps happening, please report it.
                    </p>
                    <Button variant="primary" onClick={this.handleReload}>
                        Reload
                    </Button>
                </CardBody>
            </Card>
        );
    }
}

export default ErrorBoundary;
